// Preview element inspection — arms a picker INSIDE the project iframe
// (#preview-app-iframe) and resolves each clicked node back to its DOMQL
// component key-path.
//
// USER-CODE-ORIGIN (canvas H1, 2026-10-09): the project iframe is an OPAQUE
// user-code frame (shared userCodeFrame.js) — its document is not readable
// from here. The picker therefore runs INSIDE the frame as an editor tool
// (`handle.runTool`): `framePicker` below is sent as TEXT, so it must stay
// SELF-CONTAINED (no module-scope helper, no import, ES2017 syntax) and it
// reports the picked node as plain data over the frame's port.
//
// Identity: smbls stamps every rendered node with a `data-key` attribute and, in
// most builds, a `node.ref` DOMQL back-reference whose `__ref.path` is the
// canonical key-path array. We read `ref.__ref.path` first and fall back to the
// `data-key` ancestor chain — so the picked chip shows the project path (e.g.
// Hero › Title) the assistant can act on, not the raw DOM tag path.
//
// Vendored/adapted from extensions/packages/simone/symbols/functions/pickElement.js
// (its standalone in-page picker, `startLocalPick`). Keep the describe shape in
// sync with that source so PickedContextChip + the transport consume picks
// identically across surfaces.

// The active picker's handles live on `globalThis` rather than a module-scope
// `let` — frank's globalScope-lift promotes `_stopPick` / `_startPick` into
// independently-serialized functions, which strips the shared module closure;
// a plain `let` free var then revives as a guaranteed `ReferenceError`. See
// tickets/opus.md "canvas serialization defects blocking the authorised
// production push".

// The frame to pick in: the one the caller NAMES (an <iframe> node, or the
// DOMQL element that renders it — several frames can stand on one page: a Fly
// page's site-preview cards, functions/previewAnswer.js), else /preview's own
// Scene frame by its fixed id (the Chat composer's Pick button names none).
const _getIframe = (frame) => {
  if (frame) {
    const node = frame.node && frame.node.nodeType === 1 ? frame.node : frame
    return node && node.nodeType === 1 && String(node.tagName).toLowerCase() === 'iframe'
      ? node
      : null
  }
  return typeof document !== 'undefined' ? document.getElementById('preview-app-iframe') : null
}

// Runs INSIDE the opaque frame (sent as text — self-contained). Hover
// highlights, a click reports `{ picked }`, Escape reports `{ cancel }`.
// Returns the stop function the frame calls on `tool-stop`.
export function framePicker(api) {
  var win = api.win
  var doc = api.doc
  var emit = api.emit

  function segment(n) {
    var tag = n.tagName.toLowerCase()
    var id = n.id ? '#' + n.id : ''
    var cls =
      n.className && typeof n.className === 'string'
        ? '.' + n.className.trim().split(/\s+/).slice(0, 2).join('.')
        : ''
    return tag + id + (cls === '.' ? '' : cls)
  }

  function describe(node) {
    if (!node || node.nodeType !== 1) return null
    var el = null
    var cur = node
    while (cur) {
      if (cur.ref) {
        el = cur.ref
        break
      }
      cur = cur.parentElement
    }
    var elRef = el && el.__ref
    var keyPath = elRef && Array.isArray(elRef.path) ? elRef.path.slice() : []
    if (!keyPath.length) {
      var dk = []
      var p = node
      while (p && p.nodeType === 1 && p !== doc.documentElement) {
        var k = p.getAttribute && p.getAttribute('data-key')
        if (k) dk.unshift(k)
        p = p.parentNode
      }
      if (dk.length) keyPath = dk
    }
    var tagPath = []
    var n = node
    while (n && n.nodeType === 1 && n !== doc.documentElement) {
      tagPath.unshift(segment(n))
      n = n.parentNode
    }
    var rect = node.getBoundingClientRect()
    var attrs = {}
    if (node.attributes) {
      for (var i = 0; i < node.attributes.length; i++) {
        attrs[node.attributes[i].name] = node.attributes[i].value
      }
    }
    return {
      tag: node.tagName.toLowerCase(),
      key:
        (el && (el.key || (elRef && (elRef.key || elRef.__name)))) ||
        (node.getAttribute && (node.getAttribute('data-key') || node.getAttribute('key'))) ||
        (keyPath.length ? keyPath[keyPath.length - 1] : null) ||
        null,
      component: (el && typeof el.component === 'string' && el.component) || (elRef && elRef.__name) || null,
      keyPath: keyPath.map(String),
      tagPath: tagPath,
      path: keyPath.map(String),
      text: (node.innerText || node.textContent || '').slice(0, 200),
      rect: { x: rect.left, y: rect.top, w: rect.width, h: rect.height },
      // The previewed project's own route + title, so the assistant knows
      // which page the element lives on.
      url: (win.location && win.location.href) || null,
      title: (doc && doc.title) || null,
      attrs: attrs,
      pickedAt: Date.now()
    }
  }

  // Imperative highlight overlay — lives inside the frame document (outside
  // the DOMQL tree), so it scales with the project content under device
  // presets and raw styles are the only option. pointer-events:none keeps it
  // from being the elementFromPoint hit / a pick target.
  var overlay = doc.createElement('div')
  overlay.style.cssText =
    'position:fixed;pointer-events:none;z-index:2147483647;border:2px solid #2a72ff;' +
    'background:rgba(42,114,255,0.15);transition:all 60ms ease;display:none;'
  doc.documentElement.appendChild(overlay)

  var hover = null
  function onMove(ev) {
    var target = doc.elementFromPoint(ev.clientX, ev.clientY)
    if (!target || target === hover || target === overlay) return
    hover = target
    var r = target.getBoundingClientRect()
    overlay.style.display = 'block'
    overlay.style.left = r.left + 'px'
    overlay.style.top = r.top + 'px'
    overlay.style.width = r.width + 'px'
    overlay.style.height = r.height + 'px'
  }
  function onClick(ev) {
    ev.preventDefault()
    ev.stopPropagation()
    emit({ picked: describe(ev.target) })
  }
  function onKey(ev) {
    if (ev.key === 'Escape') emit({ cancel: true })
  }
  doc.addEventListener('mousemove', onMove, true)
  doc.addEventListener('click', onClick, true)
  doc.addEventListener('keydown', onKey, true)
  return function stop() {
    doc.removeEventListener('mousemove', onMove, true)
    doc.removeEventListener('click', onClick, true)
    doc.removeEventListener('keydown', onKey, true)
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay)
  }
}

const _errorMsg = (root, text) => {
  const list = (root.chatMessages || []).concat({
    id: `err-${Date.now()}`,
    role: 'system',
    text,
    ts: Date.now()
  })
  root.update({ chatMessages: list })
}

// The pick comes from the PROJECT's own code (the frame), so it is untrusted
// input to the AI chat (security review 9: a hostile project can forge it as
// a prompt injection). Keep only the known fields, bounded, as plain strings
// and numbers, and mark it `untrusted` for the transport. Exported for tests.
export const sanitizePick = (raw) => {
  if (!raw || typeof raw !== 'object') return null
  const str = (v, max) => (typeof v === 'string' ? v.replace(/[\u0000-\u001f\u007f]/g, ' ').slice(0, max) : null)
  const list = (v, n, max) =>
    Array.isArray(v) ? v.slice(0, n).map((x) => str(x, max)).filter((x) => x !== null) : []
  const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : 0)
  const tag = str(raw.tag, 32)
  if (!tag || !/^[a-z][a-z0-9-]*$/.test(tag)) return null
  const attrs = {}
  const ra = raw.attrs && typeof raw.attrs === 'object' && !Array.isArray(raw.attrs) ? raw.attrs : {}
  for (const k of Object.keys(ra).slice(0, 20)) {
    if (!/^[A-Za-z_:][\w:.-]{0,39}$/.test(k)) continue
    const v = str(ra[k], 200)
    if (v !== null) attrs[k] = v
  }
  const keyPath = list(raw.keyPath, 24, 120)
  const r = raw.rect && typeof raw.rect === 'object' ? raw.rect : {}
  return {
    tag,
    key: str(raw.key, 120),
    component: str(raw.component, 120),
    keyPath,
    path: keyPath,
    tagPath: list(raw.tagPath, 40, 160),
    text: str(raw.text, 200) || '',
    rect: { x: num(r.x), y: num(r.y), w: num(r.w), h: num(r.h) },
    url: str(raw.url, 300),
    title: str(raw.title, 200),
    attrs,
    pickedAt: num(raw.pickedAt) || Date.now(),
    untrusted: true
  }
}

const recordPicked = (root, picked) => {
  const info = sanitizePick(picked)
  if (!info) {
    _errorMsg(root, 'Could not read that element — try a different one.')
    return
  }
  const sig = (info.tagPath || []).join('>')
  const list = (root.pickedElements || []).slice()
  if (!list.some((p) => (p.tagPath || []).join('>') === sig)) list.push(info)
  root.update({ pickedElements: list })
}

const _stopPick = () => {
  if (!globalThis.__previewPicker) return
  const { handle, parentKey } = globalThis.__previewPicker
  try {
    if (handle) handle.stopTool('pick')
    if (typeof document !== 'undefined') document.removeEventListener('keydown', parentKey, true)
  } catch (err) {
    console.warn('[preview] stop picker:', err)
  }
  globalThis.__previewPicker = null
}

// `frame`: the iframe node it arms on (kept beside the handle, so a caller can
// ask where the picker is — previewPickArmedOn). `quiet`: a caller that names
// its frame says a failure in its own place, never as a chat line.
const _startPick = (root, handle, frame, quiet) => {
  if (globalThis.__previewPicker) return true
  if (!handle || !handle.alive) {
    if (!quiet) _errorMsg(root, 'Open a project preview first, then pick an element.')
    return false
  }
  const done = () => {
    _stopPick()
    root.update({ picking: false })
  }
  // Esc may be pressed while focus sits in the parent (the chat panel), so
  // listen here too; the frame reports its own Escape as `{ cancel }`.
  const parentKey = (ev) => {
    if (ev.key === 'Escape') done()
  }
  globalThis.__previewPicker = { handle, parentKey, frame: frame || null }
  const armed = handle.runTool('pick', framePicker, (data) => {
    if (!data) return
    if (data.picked !== undefined) {
      recordPicked(root, data.picked)
      // Single-shot: a successful pick disarms the picker (parity with the
      // simone page-agent picker). User can re-arm via the Pick button.
      done()
    } else if (data.cancel) {
      done()
    } else if (data.error) {
      if (!quiet) _errorMsg(root, 'Could not start the element picker.')
      done()
    }
  })
  if (!armed) {
    globalThis.__previewPicker = null
    if (!quiet) _errorMsg(root, 'Open a project preview first, then pick an element.')
    return false
  }
  if (typeof document !== 'undefined') document.addEventListener('keydown', parentKey, true)
  return true
}

// el.call('togglePick') — fired by the Chat composer's Pick button: /preview's
// own Scene frame, exactly as before. el.call('togglePick', frame) — a caller
// that NAMES its frame (a node or its element): a picker armed on THAT frame
// stops; one armed on another frame moves here; none — it arms here. Answers
// whether a picker is armed on the named frame after the call.
export const togglePick = function (frame) {
  const root = this.state.root
  const named = frame ? _getIframe(frame) : null
  if (frame && !named) return false
  if (root.picking) {
    const armedOn = globalThis.__previewPicker ? globalThis.__previewPicker.frame : null
    _stopPick()
    root.update({ picking: false })
    if (!named || armedOn === named) return false
  }
  const iframe = named || _getIframe()
  if (!iframe) {
    _errorMsg(root, 'Open a project preview first, then pick an element.')
    return false
  }
  // the frame's live handle, through the shared accessor (the registry stays
  // private to the frame host)
  const handle = this.call('getUserCodeHandle', iframe)
  if (_startPick(root, handle, iframe, !!named)) {
    root.update({ picking: true })
    return true
  }
  return false
}

// el.call('previewPickArmedOn', frame) — is the picker armed on this frame
// (a node or its element)? A reactive caller reads `root.picking` beside it.
export const previewPickArmedOn = function (frame) {
  const node = _getIframe(frame || null)
  const armed = globalThis.__previewPicker
  return !!(frame && node && armed && armed.frame === node)
}

// el.call('inspectSelected') — the composer's Inspect button. There is no
// devtools `$0` on the preview surface, so this is an alias for arming the
// in-iframe picker. (The button only shows when root.inspectorTools is set;
// preview keeps it off and uses Pick, but the alias keeps the contract intact.)
export const inspectSelected = function () {
  return togglePick.call(this)
}
